import { useEffect, useRef, useState } from 'react';
import { Button, Input } from '@dycs/ui';
import {
  DEPARTURE_OTHER,
  DEPARTURE_OTHER_LABEL,
  LOST_TIME_SLOTS,
  effectiveRouteSchedule,
  looksLikeLostItem,
  lostRoutePlan,
  type RouteScheduleEntry,
  type TicketType,
} from '@dycs/shared';
import {
  callableMessage,
  escalate,
  requestPhoneCode,
  verifyPhoneCode,
  type ChatTurn,
} from '@/lib/firebase';

interface IntakeFormProps {
  tenantId: string;
  type: TicketType;
  routes: string[];
  /**
   * 거래처 노선·출발시간표(tenants.routeSchedule · 2026-10-13 게시판 YCAmtzH2). 노선이 1개 이상이면
   *   **분실물** 폼이 노선·출발 시간을 이 목록에서 필수로 고르게 한다(「기타 (직접 입력)」 없음).
   *   🔴 비면 지금과 완전히 같다(일반 문의는 시간표와 무관하게 현행 유지). 판정 정본 = shared `lostRoutePlan`.
   */
  routeSchedule?: RouteScheduleEntry[];
  /**
   * 자동 시간표(tenants.routeScheduleAuto · 2026-10-14 MxnsrbK8) — Buslink Ops 기사 명부의 노선·출근 시각.
   *   손입력 시간표에 시간이 없을 때만 쓰인다(`effectiveRouteSchedule`). auto 면 출발 시간에
   *   「퇴근·그 밖의 시간」 탈출구가 붙는다(명부엔 출근 시각뿐이라 퇴근길 고객이 막히면 안 된다).
   */
  routeScheduleAuto?: RouteScheduleEntry[];
  /** 챗봇 대화에서 전환된 접수인지(source='chatbot'). */
  fromChatbot?: boolean;
  /** 접수 전 챗봇 대화 — 티켓 스레드 맨 앞에 그대로 남는다(상담원이 이어서 응대). */
  chatHistory?: ChatTurn[];
  /**
   * 접수 전 휴대폰 인증번호 확인을 요구하는 창구인지(tenants.requirePhoneVerify).
   *   홈페이지처럼 불특정 다수가 들어오는 곳에서만 켠다 — 거래처 셔틀 이용자에게까지
   *   물리면 이미 신원이 확인된 사람에게 한 단계를 더 요구하는 셈이다.
   */
  requirePhoneVerify?: boolean;
  /**
   * 분실물에서 «이용 고객사» 칸을 띄울지(tenants.askCompanyName). 기본은 **안 띄운다** —
   *   거래처 전용 링크는 어느 회사인지가 이미 확정이라(헤더에도 있고 tenantId 로도 남는다)
   *   고객에게 아는 것을 한 번 더 묻는 칸이 된다. 한 거래처에 여러 회사가 묶인 곳만 켠다.
   *   인증 창구(requirePhoneVerify)는 이 값과 무관하게 뜨고 필수다.
   */
  askCompanyName?: boolean;
  /**
   * 거래처 전용 식별번호 칸의 **라벨**(tenants.employeeNoLabel). 비면 칸을 띄우지 않는다.
   *   신촌세브란스처럼 «셔틀 예약 계정 비밀번호 재설정» 문의가 대다수인 거래처에서 사번을 미리
   *   받아 두면 상담원이 계정을 특정하려고 되묻는 한 바퀴가 사라진다(2026-09-09 way).
   */
  employeeNoLabel?: string;
  /** 그 칸을 필수로 할지(기본 false=선택 — 사번과 무관한 분실물·노선 문의까지 막지 않는다). */
  employeeNoRequired?: boolean;
  /**
   * 사업장 목록(tenants.sites · 2026-10-15 게시판 sRX4v2c2). 비면 칸이 없다. 있으면 「사업장」을 **필수로** 고른다
   *   (DB하이텍 = 부천·상우 — 같은 노선 이름이 두 사업장에 다 있어 노선으로는 못 가른다).
   *   🔴 필수는 **폼에서만** — 서버(ticketCore)는 묵은 iframe 번들 때문에 거부하지 않고 목록 밖 값만 버린다.
   */
  sites?: string[];
  /**
   * 「분실물 접수로 옮기기」 — 문의 폼에서 분실물로 보이는 글이 감지됐을 때 고객이 누른다.
   *   🔴 **입력값을 그대로 안고 넘어간다** — 부모가 `type` 만 바꾸고 이 컴포넌트를 같은 자리에
   *      유지하므로 이름·연락처·내용이 살아 있다. 폼을 다시 마운트시키면(키 변경) 다 지워져
   *      "옮겼더니 처음부터"가 되고, 그건 안 옮기느니만 못하다.
   */
  onSwitchToLost?: () => void;
}

/** 인증 단계 — idle(미발송) → sent(코드 입력 중) → verified(완료). */
type VerifyStage = 'idle' | 'sent' | 'verified';

/** 드롭다운의 '기타(직접 입력)' 선택값 — 실제 노선명과 겹치지 않게 밑줄 토큰을 쓴다. */
const ROUTE_ETC = '__etc__';

/**
 * 접수 폼 — 이름·휴대폰(필수)·이메일(선택)·노선·내용·개인정보동의 → escalate Function.
 *   분실물은 분실물 필드(+고객사) 추가. 제출 성공 시 접수번호(ticketId) 안내.
 *   requirePhoneVerify 창구는 휴대폰 인증을 통과해야 접수 버튼이 열린다.
 */
export function IntakeForm({
  tenantId,
  type,
  routes,
  routeSchedule,
  routeScheduleAuto,
  fromChatbot,
  chatHistory,
  requirePhoneVerify = false,
  askCompanyName = false,
  employeeNoLabel = '',
  employeeNoRequired = false,
  sites = [],
  onSwitchToLost,
}: IntakeFormProps) {
  const isLost = type === 'lost_item';
  const hasRouteList = routes.length > 0;
  // 인증 창구는 어느 회사 셔틀인지 알 길이 없어 늘 묻는다(그리고 필수).
  const companyShown = isLost && (requirePhoneVerify || askCompanyName);
  // 식별번호(사번·학번) 칸 — 라벨 자체가 스위치다. 문의·분실물 **양쪽**에 뜬다(계정 확인은
  // 분실물 접수자에게도 필요할 수 있고, 유형별로 갈라 두면 켠 쪽이 안 보이는 사고가 난다).
  const employeeNoShown = employeeNoLabel.trim().length > 0;

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [route, setRoute] = useState('');
  // 등록 노선에 없는 노선을 적는 칸 — 목록이 없는 거래처에서는 이 칸 하나만 뜬다.
  const [routeFree, setRouteFree] = useState('');
  const [content, setContent] = useState('');
  const [consent, setConsent] = useState(false);
  // 분실물 전용
  const [itemName, setItemName] = useState('');
  const [lostDate, setLostDate] = useState('');
  // 분실 시간대 — 날짜와 한 쌍으로 **둘 다 필수**(2026-09-10 way · 전 거래처). 같은 노선도
  // 하루 여러 회차라 날짜만 받으면 상담원이 «몇 시 차였나»를 매번 되묻는다.
  const [lostTime, setLostTime] = useState('');
  // 출발 시간 — 시간표가 있는 거래처의 분실물에서만(고른 노선의 times 중 하나). 노선을 바꾸면 비운다.
  const [departureTime, setDepartureTime] = useState('');
  const [lostLocation, setLostLocation] = useState('');
  // 고객사 — 인증 창구(홈페이지)에서는 필수: 어느 회사 셔틀인지 모르면 상담원이 어느 차량·어느
  // 보관함을 뒤질지 결정할 수 없다(2026-08-26 way). 그 외 거래처 전용 링크에서는 기본으로 감춘다
  // (askCompanyName) — 링크에 이미 회사가 박혀 있어 되풀이가 된다(2026-09-08 way).
  const [companyName, setCompanyName] = useState('');
  // 거래처 전용 식별번호(사번·학번) — employeeNoLabel 이 있는 거래처에서만 뜬다.
  const [employeeNo, setEmployeeNo] = useState('');
  // 사업장 — sites 가 있는 거래처에서만(문의·분실물 양쪽). 사업장이 하나뿐이면 고를 게 없으니 그 값으로 둔다.
  const sitesShown = sites.length > 0;
  const [site, setSite] = useState(sites.length === 1 ? sites[0]! : '');

  // ── 휴대폰 인증 상태 ──────────────────────────────────────
  const [stage, setStage] = useState<VerifyStage>('idle');
  const [verifyId, setVerifyId] = useState('');
  const [verifyToken, setVerifyToken] = useState('');
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [verifyMsg, setVerifyMsg] = useState<string | null>(null);
  const [verifyErr, setVerifyErr] = useState<string | null>(null);
  const [leftSec, setLeftSec] = useState(0);
  const timerRef = useRef<number | null>(null);

  // 「분실물 접수 아닌가요?」 제안을 고객이 닫았는지 — 한 번 닫으면 이 세션에선 다시 안 뜬다.
  //   계속 따라다니는 제안은 곧 배경이 되고, 배경이 된 안내는 없는 것과 같다.
  const [lostHintDismissed, setLostHintDismissed] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null); // ticketId

  // 남은 유효시간 카운트다운 — 만료를 화면에 보여 주지 않으면 고객은 "왜 안 되지"만 반복한다.
  useEffect(() => {
    if (stage !== 'sent' || leftSec <= 0) return;
    timerRef.current = window.setTimeout(() => setLeftSec((s) => s - 1), 1000);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [stage, leftSec]);

  // 🔴 국내 휴대폰(01x)만 — 서버 phoneVerify `isKoreanMobile` 과 같은 자(2026-10-12 way).
  //    회신 알림톡은 휴대폰으로만 간다. '+82' 표기는 접어서 본다.
  const digits = (() => {
    const d = phone.replace(/[^0-9]/g, '');
    if (d.startsWith('820')) return d.slice(2);
    if (d.startsWith('82')) return '0' + d.slice(2);
    return d;
  })();
  const phoneLooksOk = /^01[016789][0-9]{7,8}$/.test(digits);
  // 인증 창구에서 인증 전이면 접수 자체가 불가 — 버튼을 눌러 보고 거절당하게 두지 않는다.
  const verifyBlocked = requirePhoneVerify && stage !== 'verified';

  // ── 탑승 노선 ────────────────────────────────────────────
  // 등록 노선이 없으면 자유 입력으로 받는다. 예전 구글 폼이 그랬고(자유 기입) 실제로
  // 이관분 1,843건 중 대부분이 노선을 남겼다 — 목록이 비었다고 칸을 없애면 상담원이
  // 접수 후 노선을 되묻는 한 바퀴가 매번 생긴다(2026-09-08 김석진).
  // 🔴 시간표 모드(분실물 + 거래처 시간표에 노선 1개 이상 · 2026-10-13 YCAmtzH2): 노선은 시간표 목록에서만
  //    고른다 — 고객이 적은 노선명이 거래처 실제 노선명과 안 맞아 기사님이 확인하기 어려웠다(CS 강유정).
  //    고른 노선에 출발 시간이 있으면 「출발 시간」 필수 + 「분실 시간대」 숨김, 없으면 분실 시간대로 폴백.
  //    시간표가 없으면 plan.mode === 'legacy' → 아래 판정이 전부 예전 그대로다.
  //    유효 시간표 = 손입력(시간 1개+) > 기사 명부 자동 > 없음 — 판정 정본 shared `effectiveRouteSchedule`.
  //    auto 면 출발 시간 끝에 「퇴근·그 밖의 시간」 → 고르면 분실 시간대 필수·출발 시간 미저장(2026-10-14).
  const effSchedule = effectiveRouteSchedule({ routeSchedule, routeScheduleAuto });
  const plan = lostRoutePlan(isLost ? effSchedule.schedule : [], route, effSchedule.source, departureTime);
  const schedMode = isLost && plan.mode === 'schedule';
  // 고른 노선의 선택지에 없는 출발 시간은 «안 고른 것»(노선을 바꾼 직후 등).
  const departureValue = schedMode && plan.times.includes(departureTime) ? departureTime : '';
  const lostTimeShown = isLost && (!schedMode || plan.showLostTimeSlot);
  const routeFreeShown = !schedMode && (!hasRouteList || route === ROUTE_ETC);
  const routeValue = schedMode ? plan.selectedRoute : (routeFreeShown ? routeFree : route).trim();
  // 🔴 문의·분실물 모두 목록 유무와 무관하게 필수(2026-09-22 way). 등록 노선이 없는 14곳의
  //    고객문의는 선택이라 절반(115건 중 59건)이 노선 없이 들어왔고, 적더라도 «7시 판교 출근
  //    버스»처럼 같은 시각 두 노선(판교역·미금역 셔틀)을 못 가르는 값이 와서 상담원이 되물었다.
  //    코어(ticketCore)엔 안 넣는다 — 분실 일자와 같은 이유(묵은 iframe 번들 · issues-ui 참조).
  const routeRequired = true;

  // ── 「분실물 접수 아닌가요?」 제안 (2026-09-10 way) ─────────────
  // prod 실측: 고객문의 1,636건 중 **52건(3.2%)이 실은 분실물**이었다 — 그 창구엔 분실 일자·
  // 시간대 칸이 없어 상담원이 매번 되물었다(발단 = 한화시스템 EVMH2H 지갑).
  // 🔴 **제안이지 분류가 아니다** — 자동으로 유형을 바꾸지 않고, 접수를 막지도 않는다.
  //    틀린 제안은 한 줄 무시로 끝나지만, 틀린 강제 전환은 고객을 잃는다.
  const lostHintShown =
    !isLost && !!onSwitchToLost && !lostHintDismissed && looksLikeLostItem(content);

  if (done) {
    return (
      <div className="text-center py-6 space-y-2">
        <div className="text-2xl">✅</div>
        <p className="text-sm font-medium">접수가 완료되었습니다.</p>
        <p className="text-xs text-text-tertiary">
          접수번호 <span className="font-mono text-text-secondary">{done.slice(0, 8)}</span>
        </p>
        <p className="text-xs text-text-tertiary">
          담당 상담원이 확인 후 등록하신 연락처로 회신드리겠습니다.
        </p>
      </div>
    );
  }

  /** 인증번호 발송 요청. */
  async function handleSendCode() {
    if (sending) return;
    if (!phoneLooksOk) {
      setVerifyErr('휴대폰 번호를 정확히 입력해 주세요. (예: 010-0000-0000)');
      return;
    }
    setVerifyErr(null);
    setVerifyMsg(null);
    setSending(true);
    try {
      const res = await requestPhoneCode({ tenantId, phone });
      setVerifyId(res.verifyId);
      setStage('sent');
      setCode('');
      setLeftSec(res.expiresInSec);
      setVerifyMsg(
        res.channel === 'alimtalk'
          ? '카카오톡으로 인증번호를 보냈습니다.'
          : '문자로 인증번호를 보냈습니다.',
      );
    } catch (err) {
      console.error('[widget] requestPhoneCode 실패', err);
      // 서버가 보낸 사유(대기 시간·요청 과다·번호 오류)를 그대로 보여 준다.
      setVerifyErr(callableMessage(err, '인증번호를 보내지 못했습니다. 잠시 후 다시 시도해 주세요.'));
    } finally {
      setSending(false);
    }
  }

  /** 입력한 인증번호 확인 → 1회용 토큰 수령. */
  async function handleCheckCode() {
    if (checking) return;
    if (code.replace(/[^0-9]/g, '').length !== 6) {
      setVerifyErr('인증번호 6자리를 입력해 주세요.');
      return;
    }
    setVerifyErr(null);
    setChecking(true);
    try {
      const res = await verifyPhoneCode({ verifyId, code });
      setVerifyToken(res.verifyToken);
      setStage('verified');
      setVerifyMsg('휴대폰 인증이 완료되었습니다.');
      setLeftSec(0);
    } catch (err) {
      console.error('[widget] verifyPhoneCode 실패', err);
      setVerifyErr(callableMessage(err, '인증에 실패했습니다. 다시 시도해 주세요.'));
    } finally {
      setChecking(false);
    }
  }

  /**
   * 번호를 고치면 인증은 **무효**다. 화면에만 ✓ 를 남겨 두면 접수 단계에서 서버가
   * "인증받은 번호와 접수 번호가 다릅니다"로 거절해 고객은 영문을 모른다.
   */
  function handlePhoneChange(next: string) {
    setPhone(next);
    if (stage !== 'idle') {
      setStage('idle');
      setVerifyId('');
      setVerifyToken('');
      setCode('');
      setLeftSec(0);
      setVerifyMsg(null);
      setVerifyErr(null);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!consent || submitting) return;
    // 🔴 「ㅇㅇㅇ」·같은 글자 반복·한 글자 = 이름을 안 적은 꼴(2026-10-13 way · onsemi).
    //    서버 ticketCore `nameProblem` 과 같은 자 — 한쪽만 고치지 말 것.
    {
      const s = name.replace(/[ \t]/g, '');
      if (!s) {
        setError('이름을 입력해 주세요.');
        return;
      }
      if (/^[ㄱ-ㅎㅏ-ㅣ]+$/.test(s) || /^(.)\1+$/.test(s) || s.length < 2 || !/[가-힣a-zA-Z]/.test(s)) {
        setError('이름을 정확히 입력해 주세요. 회신·확인 연락에 사용됩니다.');
        return;
      }
    }
    // 휴대폰 필수(2026-08-26 way) — 문의·분실물 모두. 회신 수단이 이메일뿐이면 알림톡이 못 나가고
    // 분실물은 되찾을 때 전화 한 통이 필요한데 번호가 없으면 상담원이 붙잡을 실마리가 없다.
    // 형식 = 국내 휴대폰(01x)만(2026-10-12 way) — 회신 알림톡이 갈 수 있는 번호여야 받은 의미가 있다.
    if (!phone.trim()) {
      setError('휴대폰 번호를 입력해 주세요.');
      return;
    }
    if (!phoneLooksOk) {
      setError('휴대폰 번호를 정확히 입력해 주세요. (예: 010-0000-0000)');
      return;
    }
    if (verifyBlocked) {
      setError('휴대폰 인증을 먼저 완료해 주세요.');
      return;
    }
    if (!content.trim()) {
      setError(isLost ? '분실 상황을 입력해 주세요.' : '문의 내용을 입력해 주세요.');
      return;
    }
    // 노선 필수(2026-08-06 킥오프 회의) — 노선을 모르면 상담원이 기사·차량을 못 찾아
    // 되묻느라 응대가 한 바퀴 더 돈다. 등록 노선이 없는 거래처는 자유 입력으로 받는다.
    if (routeRequired && !routeValue) {
      setError(routeFreeShown ? '탑승 노선을 입력해 주세요.' : '탑승 노선을 선택해 주세요.');
      return;
    }
    // 🔴 분실 일자·시간대 필수(2026-09-10 way · 전 거래처) — 노선 하나에 하루 여러 회차가 있어
    //    «어느 차량을 뒤질지»는 노선+날짜+시간대 셋이 모여야 정해진다. 시간대는 '기억나지 않음'
    //    선택지가 있어 아무도 접수 자체를 못 하게 되지는 않는다(모르는 것과 안 물어본 것은 다르다).
    if (isLost && !lostDate) {
      setError('분실하신 날짜를 선택해 주세요.');
      return;
    }
    // 출발 시간(시간표 모드) — 위젯에서만 필수. 서버(ticketCore)는 묵은 iframe 번들 때문에 막지 않는다.
    if (schedMode && plan.departureRequired && !departureValue) {
      setError('출발 시간을 선택해 주세요.');
      return;
    }
    if (lostTimeShown && !lostTime) {
      setError('분실 시간대를 선택해 주세요.');
      return;
    }
    // 홈페이지 분실물은 고객사가 사실상 유일한 단서다(노선 목록이 없는 창구라 route 로도 못 좁힌다).
    if (isLost && requirePhoneVerify && !companyName.trim()) {
      setError('이용하신 고객사(회사·기관)를 입력해 주세요.');
      return;
    }
    // 식별번호 필수 거래처 — 서버(ticketCore)가 같은 판정을 한 번 더 한다(여기는 안내용).
    if (employeeNoShown && employeeNoRequired && !employeeNo.trim()) {
      setError(`${employeeNoLabel}을(를) 입력해 주세요.`);
      return;
    }
    // 사업장 — 위젯에서만 필수(서버는 거부하지 않는다 · 위 sites 주석).
    if (sitesShown && !sites.includes(site)) {
      setError('사업장을 선택해 주세요.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const res = await escalate({
        tenantId,
        type,
        customerName: name,
        customerEmail: email,
        customerPhone: phone,
        content,
        route: routeValue || undefined,
        ...(employeeNoShown && employeeNo.trim() ? { employeeNo: employeeNo.trim() } : {}),
        ...(sitesShown && site ? { site } : {}),
        privacyConsent: true,
        fromChatbot,
        ...(chatHistory && chatHistory.length > 0 ? { chatHistory } : {}),
        ...(verifyToken ? { verifyId, verifyToken } : {}),
        ...(isLost
          ? {
              itemName: itemName || undefined,
              lostDate: lostDate || undefined,
              // 보이지 않은 칸의 값은 보내지 않는다(출발 시간을 고르면 시간대는 묻지 않았다).
              lostTime: lostTimeShown ? lostTime || undefined : undefined,
              departureTime: departureValue || undefined,
              lostLocation: lostLocation || undefined,
              companyName: companyName || undefined,
            }
          : {}),
      });
      setDone(res.ticketId);
    } catch (err) {
      console.error('[widget] escalate 실패', err);
      // 인증 만료·중복 사용처럼 **고객이 조치할 수 있는** 거절 사유는 그대로 보여 준다.
      setError(callableMessage(err, '접수에 실패했습니다. 잠시 후 다시 시도해 주세요.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="space-y-3" onSubmit={handleSubmit}>
      <Input
        label="이름"
        placeholder="홍길동"
        required
        value={name}
        onChange={(e) => setName(e.target.value)}
      />

      {/* 휴대폰 — 인증 창구에서는 입력칸 옆에 '인증번호 받기'가 붙는다. */}
      <div>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Input
              label="휴대폰"
              type="tel"
              inputMode="tel"
              placeholder="010-0000-0000"
              required
              value={phone}
              onChange={(e) => handlePhoneChange(e.target.value)}
            />
          </div>
          {requirePhoneVerify && stage !== 'verified' && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="mb-[1px] whitespace-nowrap"
              loading={sending}
              disabled={!phoneLooksOk}
              onClick={() => void handleSendCode()}
            >
              {stage === 'sent' ? '재발송' : '인증번호 받기'}
            </Button>
          )}
          {requirePhoneVerify && stage === 'verified' && (
            <span className="mb-2.5 text-xs font-medium text-success whitespace-nowrap">
              ✓ 인증완료
            </span>
          )}
        </div>

        {requirePhoneVerify && stage === 'sent' && (
          <div className="mt-2 flex items-end gap-2">
            <div className="flex-1">
              <Input
                label="인증번호"
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="6자리"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
              />
            </div>
            <Button
              type="button"
              size="sm"
              className="mb-[1px] whitespace-nowrap"
              loading={checking}
              onClick={() => void handleCheckCode()}
            >
              확인
            </Button>
          </div>
        )}

        {requirePhoneVerify && stage === 'sent' && leftSec > 0 && (
          <p className="mt-1 text-xs text-text-tertiary">
            남은 시간 {Math.floor(leftSec / 60)}:{String(leftSec % 60).padStart(2, '0')}
          </p>
        )}
        {requirePhoneVerify && stage === 'sent' && leftSec === 0 && (
          <p className="mt-1 text-xs text-warning">
            인증번호가 만료되었습니다. 재발송해 주세요.
          </p>
        )}
        {verifyMsg && !verifyErr && <p className="mt-1 text-xs text-text-secondary">{verifyMsg}</p>}
        {verifyErr && <p className="mt-1 text-xs text-danger">{verifyErr}</p>}
      </div>

      <Input
        label="이메일 (선택)"
        type="email"
        placeholder="name@example.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />

      {/* 거래처 전용 식별번호(사번·학번) — 거래처 설정에 라벨이 있을 때만.
          🔴 계정 관련 문의(비밀번호 재설정 등)는 이 값이 없으면 상담원이 계정을 특정할 수 없어
             접수 뒤 되묻는 한 바퀴가 반드시 돈다. 그 되물음을 폼으로 당겨 온 칸이다. */}
      {employeeNoShown && (
        <div>
          <Input
            label={employeeNoLabel}
            inputMode="text"
            placeholder="예: 132424"
            required={employeeNoRequired}
            value={employeeNo}
            onChange={(e) => setEmployeeNo(e.target.value)}
          />
          {!employeeNoRequired && (
            <p className="mt-1 text-xs text-text-tertiary">
              비밀번호 재설정 등 본인 계정 확인이 필요한 문의는 꼭 적어 주세요. 없으면 비워 두셔도
              됩니다.
            </p>
          )}
        </div>
      )}

      {/* 사업장 — 거래처 설정(tenants.sites)이 있을 때만 · 필수 · 노선 칸보다 위(2026-10-15 sRX4v2c2).
          같은 노선 이름이 여러 사업장에 있어 노선만으로는 어느 사업장 문의인지 알 수 없다. */}
      {sitesShown && (
        <div>
          <span className="block mb-1 text-xs font-medium text-text-secondary">
            사업장<span className="text-danger"> *</span>
          </span>
          <select
            className="w-full px-3 py-2 text-sm rounded-md bg-surface border border-border-default focus:border-accent focus:outline-none"
            value={site}
            onChange={(e) => setSite(e.target.value)}
            required
          >
            <option value="">선택해 주세요</option>
            {sites.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* 탑승 노선 — 등록 노선이 있으면 드롭다운(+기타 직접입력), 없으면 자유 입력.
          목록이 비면 칸을 통째로 감추던 탓에 전 거래처에서 이 칸이 한 번도 뜨지 않았다. */}
      <div>
        <span className="block mb-1 text-xs font-medium text-text-secondary">
          탑승 노선{routeRequired && <span className="text-danger"> *</span>}
        </span>
        {schedMode && (
          <select
            className="w-full px-3 py-2 text-sm rounded-md bg-surface border border-border-default focus:border-accent focus:outline-none"
            value={plan.selectedRoute}
            onChange={(e) => {
              setRoute(e.target.value);
              setDepartureTime('');
            }}
            required
          >
            <option value="">선택해 주세요</option>
            {plan.routeOptions.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        )}
        {!schedMode && hasRouteList && (
          <select
            className="w-full px-3 py-2 text-sm rounded-md bg-surface border border-border-default focus:border-accent focus:outline-none"
            value={route}
            onChange={(e) => setRoute(e.target.value)}
            required={routeRequired}
          >
            <option value="">선택해 주세요</option>
            {routes.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
            <option value={ROUTE_ETC}>기타 (직접 입력)</option>
          </select>
        )}
        {routeFreeShown && (
          <input
            type="text"
            className={`w-full px-3 py-2 text-sm rounded-md bg-surface border border-border-default focus:border-accent focus:outline-none placeholder:text-text-disabled${
              hasRouteList ? ' mt-2' : ''
            }`}
            placeholder="예: 미금역 셔틀 / 07:00 미금역 출발 출근버스"
            value={routeFree}
            onChange={(e) => setRouteFree(e.target.value)}
            required={routeRequired}
          />
        )}
        {routeFreeShown && (
          <p className="mt-1 text-xs text-text-tertiary">
            노선명이 정확하지 않아도 됩니다 — <strong>타시는 정류장(출발지)</strong>과 시간을 함께
            적어 주세요. 같은 시간에 여러 노선이 다닐 수 있습니다.
          </p>
        )}
      </div>

      {companyShown && (
        <Input
          label="이용 고객사"
          placeholder="예: 서울대학교 / 코오롱"
          required={requirePhoneVerify}
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
        />
      )}

      {isLost && (
        <>
          <Input
            label="분실물품"
            placeholder="예: 검정 지갑"
            value={itemName}
            onChange={(e) => setItemName(e.target.value)}
          />
          <Input
            label="분실 일자"
            type="date"
            required
            value={lostDate}
            onChange={(e) => setLostDate(e.target.value)}
          />
          {/* 출발 시간 — 거래처 시간표가 있을 때만(2026-10-13). 고른 노선의 출발 시간 중 하나를 고른다.
              노선을 아직 안 골랐으면 잠가 두고 «노선을 먼저» 안내만 한다. */}
          {schedMode && plan.showDeparture && (
            <div>
              <span className="block mb-1 text-xs font-medium text-text-secondary">
                출발 시간<span className="text-danger"> *</span>
              </span>
              <select
                className="w-full px-3 py-2 text-sm rounded-md bg-surface border border-border-default focus:border-accent focus:outline-none disabled:opacity-60"
                value={plan.otherPicked ? DEPARTURE_OTHER : departureValue}
                onChange={(e) => setDepartureTime(e.target.value)}
                disabled={!plan.selectedRoute}
                required={plan.departureRequired}
              >
                <option value="">{plan.selectedRoute ? '선택해 주세요' : '탑승 노선을 먼저 선택해 주세요'}</option>
                {plan.times.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
                {/* 자동 시간표(기사 명부 = 출근 시각뿐)일 때만 — 퇴근길 고객의 탈출구. 값은 저장되지 않는다. */}
                {plan.offerOther && <option value={DEPARTURE_OTHER}>{DEPARTURE_OTHER_LABEL}</option>}
              </select>
              <p className="mt-1 text-xs text-text-tertiary">
                {plan.otherPicked
                  ? '아래에서 분실 시간대를 골라 주세요.'
                  : plan.offerOther
                    ? `타신 버스의 출발 시간을 골라 주세요. 퇴근길이면 「${DEPARTURE_OTHER_LABEL}」을 고르세요.`
                    : '타신 버스의 출발 시간을 골라 주세요.'}
              </p>
            </div>
          )}
          {/* 분실 시간대 — 분 단위로 묻지 않는다(고객은 정확히 기억 못 한다). 1시간 구간이면
              노선+날짜와 합쳐 회차가 사실상 특정된다. '기억나지 않음'도 답이다(빈칸과 다르다).
              시간표 모드에서 출발 시간이 있는 노선을 고르면 숨는다(출발 시간이 더 정확하다). */}
          {lostTimeShown && (
          <div>
            <span className="block mb-1 text-xs font-medium text-text-secondary">
              분실 시간대<span className="text-danger"> *</span>
            </span>
            <select
              className="w-full px-3 py-2 text-sm rounded-md bg-surface border border-border-default focus:border-accent focus:outline-none"
              value={lostTime}
              onChange={(e) => setLostTime(e.target.value)}
              required
            >
              <option value="">선택해 주세요</option>
              {LOST_TIME_SLOTS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-text-tertiary">
              버스에서 내리신 시각 기준으로 골라 주세요. 정확하지 않아도 됩니다.
            </p>
          </div>
          )}
          <Input
            label="분실 위치"
            placeholder="예: 좌석 12번 부근"
            value={lostLocation}
            onChange={(e) => setLostLocation(e.target.value)}
          />
        </>
      )}

      <label className="block">
        <span className="block mb-1 text-xs font-medium text-text-secondary">
          {isLost ? '분실 상황 상세' : '문의 내용'}
        </span>
        <textarea
          className="w-full px-3 py-2 text-sm rounded-md bg-surface border border-border-default focus:border-accent focus:outline-none min-h-24"
          placeholder="내용을 입력하세요."
          value={content}
          onChange={(e) => setContent(e.target.value)}
        />
      </label>

      {/* 분실물로 보이는 문의 — 옮기면 노선·분실일자·시간대를 함께 받는 창구가 된다.
          입력값은 그대로 따라간다(부모가 type 만 바꾼다). 닫으면 이 세션에선 다시 안 뜬다. */}
      {lostHintShown && (
        <div className="rounded-md border border-accent bg-accent-softer px-3 py-2.5">
          <p className="text-xs text-text-secondary leading-relaxed">
            혹시 <strong className="text-text-primary">버스에 두고 내리신 물건</strong>인가요?
            분실물 접수로 옮기시면 <strong className="text-text-primary">노선·분실 일자·시간대</strong>를
            함께 남기실 수 있어 훨씬 빨리 찾아 드립니다.
          </p>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              className="px-2.5 py-1.5 rounded-md text-xs font-medium bg-accent text-accent-fg"
              onClick={() => onSwitchToLost?.()}
            >
              분실물 접수로 옮기기
            </button>
            <button
              type="button"
              className="px-2 py-1.5 text-xs text-text-tertiary hover:text-text-primary"
              onClick={() => setLostHintDismissed(true)}
            >
              아니요, 문의할게요
            </button>
          </div>
        </div>
      )}

      <label className="flex items-start gap-2 text-xs text-text-secondary">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
        />
        <span>개인정보 수집·이용에 동의합니다. (접수·회신 목적)</span>
      </label>

      {error && <p className="text-xs text-danger">{error}</p>}

      <Button full type="submit" disabled={!consent || verifyBlocked} loading={submitting}>
        접수하기
      </Button>
      {verifyBlocked && (
        <p className="text-center text-xs text-text-tertiary">
          휴대폰 인증을 완료하시면 접수할 수 있습니다.
        </p>
      )}
    </form>
  );
}
